/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx } from '@emotion/react'
import { Fragment, type FunctionComponent } from 'react'

import { EuiCallOut, EuiFlexGroup, EuiSpacer } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

import { type ActionError, ActionType, type Budget } from '../../types'

const errorMessages = {
  [ActionType.Activate]: {
    title: {
      id: 'billing.notifications.budgets.table.actions.toggle.activate.error.title',
      defaultMessage: 'We could not activate the budget "{budgetName}"',
    },
    description: {
      id: 'billing.notifications.budgets.table.actions.toggle.activate.error.description',
      defaultMessage:
        'An error occurred while activating the budget. Please try again. If this keeps happening, contact your administrator.',
    },
  },
  [ActionType.Deactivate]: {
    title: {
      id: 'billing.notifications.budgets.table.actions.toggle.deactivate.error.title',
      defaultMessage: 'We could not deactivate the budget "{budgetName}"',
    },
    description: {
      id: 'billing.notifications.budgets.table.actions.toggle.deactivate.error.description',
      defaultMessage:
        'An error occurred while deactivating the budget. Please try again. If this keeps happening, contact your administrator.',
    },
  },
  [ActionType.Delete]: {
    title: {
      id: 'billing.notifications.budgets.table.actions.delete.error.title',
      defaultMessage: 'We could not delete the budget "{budgetName}"',
    },
    description: {
      id: 'billing.notifications.budgets.table.actions.delete.error.description',
      defaultMessage:
        'An error occurred while deleting the budget. Please try again. If this keeps happening, contact your administrator.',
    },
  },
}

interface Props {
  errors: ActionError
  onCleanActionError: (budgetId: Budget['id']) => void
}

const ActionErrors: FunctionComponent<Props> = ({ errors, onCleanActionError }) => {
  if (Object.keys(errors).length === 0) {
    return null
  }

  const handleCleanActionError = (budgetId: Budget['id']) => {
    onCleanActionError(budgetId)
  }

  return (
    <Fragment>
      <EuiFlexGroup direction='column' gutterSize='m'>
        {Object.keys(errors).map((key) => (
          <EuiCallOut
            key={key}
            data-test-subj='billing.notifications.budgets.table.action-error.callout'
            announceOnMount={true}
            title={
              <FormattedMessage
                {...errorMessages[errors[key]?.type as ActionType].title}
                values={{ budgetName: errors[key]?.budgetName }}
              />
            }
            color='danger'
            iconType='alert'
            onDismiss={() => handleCleanActionError(key as unknown as Budget['id'])}
          >
            <FormattedMessage {...errorMessages[errors[key]?.type as ActionType].description} />
          </EuiCallOut>
        ))}
      </EuiFlexGroup>
      <EuiSpacer size='m' />
    </Fragment>
  )
}

export default ActionErrors
